import { useId } from "react";
import type { Experiment } from "../../types/experiment";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Input } from "../ui/input";

interface DebugConfigEditorProps {
  value: Experiment["debug_config"];
  onChange: (config: Experiment["debug_config"]) => void;
}

export function DebugConfigEditor({ value, onChange }: DebugConfigEditorProps) {
  const id = useId();

  const defaults = {
    enable_decision_logging: false,
    metrics_sample_rate: 0,
    log_context_on_failure: false,
    log_raw_headers: false,
    tag_filter_metrics: false,
    decision_log_sample_rate: 0,
  };

  const cfg = { ...defaults, ...value };

  const update = (patch: Partial<NonNullable<Experiment["debug_config"]>>) => {
    onChange({ ...cfg, ...patch });
  };

  const toggles = [
    { key: "enable_decision_logging" as const, label: "Decision Logging" },
    { key: "log_context_on_failure" as const, label: "Log on Failure" },
    { key: "log_raw_headers" as const, label: "Raw Headers" },
    { key: "tag_filter_metrics" as const, label: "Tag Filter Metrics" },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {toggles.map(({ key, label }) => (
          <label key={key} htmlFor={`${id}-${key}`} className="flex items-center gap-2 cursor-pointer">
            <Switch
              id={`${id}-${key}`}
              checked={cfg[key]}
              onCheckedChange={(v) => update({ [key]: v })}
            />
            <Label htmlFor={`${id}-${key}`} className="text-xs font-normal cursor-pointer">{label}</Label>
          </label>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] text-overlay1 mb-1 block">Metrics Sample Rate</Label>
          <Input
            type="number"
            value={cfg.metrics_sample_rate}
            onChange={(e) => update({ metrics_sample_rate: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div>
          <Label className="text-[10px] text-overlay1 mb-1 block">Decision Log Sample Rate</Label>
          <Input
            type="number"
            value={cfg.decision_log_sample_rate}
            onChange={(e) => update({ decision_log_sample_rate: parseFloat(e.target.value) || 0 })}
          />
        </div>
      </div>
    </div>
  );
}
